/**
 * Tipos compartidos con el backend DeCA.
 * Mantener sincronizado con `deca-backend/src/.../<entidad>.dto.ts`.
 */

// ============================================================
// Unidad de medida (cantidad transportada)
// ============================================================

/**
 * Unidad de medida en la que se declara la `cantidad` de un Servicio.
 *
 * El modelo anterior (`pesoKg`) solo admitia kg hard-coded. Esto
 * dejaba fuera a transportistas de granel (arena, cereal, agua) y a
 * quienes facturan por palets, litros o toneladas. Sprint X.Y
 * introduce esta enum para que un mismo campo `cantidad` admita
 * varias unidades. Las conversiones no se calculan en la app: si un
 * cliente declara 1 t de grava y la MMA del vehiculo es 12 t, esa
 * conversion la hace el conductor a ojo. Aqui solo guardamos el dato
 * firmado en el DeCA.
 *
 * Mantener sincronizado con `deca-backend/src/servicio/dto/servicio.dto.ts`
 * y con `deca-frontend/src/api/types.ts` (las tres copias son la misma
 * fuente de verdad para evitar derives entre front y back).
 */
export type UnidadCantidad = 'KG' | 'T' | 'M3' | 'L' | 'PALET';

/**
 * Etiqueta humana de cada unidad (UI y PDF). Misma fuente que en el
 * backend y que en `deca-frontend/src/api/types.ts` para que nunca se
 * desincronicen.
 */
export const UNIDAD_CANTIDAD_LABEL: Record<UnidadCantidad, string> = {
  KG: 'kg',
  T: 't',
  M3: 'm³',
  L: 'L',
  PALET: 'palets',
};

/**
 * Significado humano completo de cada unidad (sin abreviar).
 *
 * Se usa como `hint` en los dropdowns o bajo la abreviatura en los
 * resúmenes para evitar dudas: "m³" vs "M3" vs "mililitros" o "PALET"
 * vs "palet europeo".
 */
export const UNIDAD_CANTIDAD_MEANING: Record<UnidadCantidad, string> = {
  KG: 'kilogramos',
  T: 'toneladas métricas',
  M3: 'metros cúbicos',
  L: 'litros',
  PALET: 'palets',
};

// ============================================================
// DeCA
// ============================================================

export interface DeCaVersion {
  version: number;
  pdfBlobPath: string;
  fechaCreacion: string;
  fechaModificacion?: string;
  motivoModificacion?: string;
  metodoModificacion?: 'A' | 'B';
  codigoPublico: string;
  activo: boolean;
  envioSnapshot?: Record<string, unknown>;
}

export interface DeCaEnriched {
  id: string;
  tenantId: string;
  envioId: string;
  /**
   * Token público del QR (URL-safe). Estable durante todo el ciclo de vida
   * del DeCA hasta una modificación, donde se regenera para invalidar el
   * QR anterior. Se usa para construir el link del QR y el endpoint
   * público `/public/deca/:codigoPublico`.
   */
  codigoPublico: string;
  /**
   * Código humano del DeCA con formato "YYYY-NNNN" (correlativo anual
   * por tenant). Lo computa el backend en `DeCaService.formatCodigoAnual`
   * a partir de `numeroAnual` y `fechaEmision`. Es lo que se muestra al
   * usuario en dashboards, listas, PDFs. Si el DeCA se creó antes de
   * Sprint X.1 y no tiene `numeroAnual`, el backend devuelve `'—'`.
   */
  codigoAnual?: string;
  numeroAnual?: number;
  /**
   * Estado legal del DeCA. Sprint 7.2: simplificado a 4 valores reales.
   * Coincide con `EstadoDeCa` del backend. Los DeCAs modificados se
   * detectan por `versiones.length >= 2`, no por un estado aparte.
   */
  estado: 'VIGENTE' | 'EN_CURSO' | 'FINALIZADO' | 'CANCELADO';
  pdfUrl?: string;
  qrUrl?: string;
  fechaEmision: string;
  fechaServicio?: string;
  fechaFinServicio?: string;
  vehiculoId: string;
  conductorId: string;
  cargadorId: string;
  transportistaId: string;
  versiones: DeCaVersion[];
  /** Auditoría — presente si el backend lo expone. */
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  /**
   * Envío asociado al DeCA, hidratado por `DeCaService.enrich`.
   * Sprint X: el envío es la fuente del estado del DeCA. Incluye todos
   * los campos del Envío (tipoServicio, fechas, observaciones, etc).
   */
  envio?: Envio;
  /**
   * Servicios del DeCA, hidratados con origen/destino del catálogo.
   * Sprint X: el DeCA tiene 1..N servicios; los datos de ruta + material
   * + bultos viven aquí (no en el Envío).
   */
  servicios?: Array<Servicio & { origen?: OrigenDestino; destino?: OrigenDestino }>;
  vehiculo?: Vehiculo;
  conductor?: Conductor;
  cargador?: Empresa;
  transportista?: Empresa;
}

export interface ModificarDeCAInput {
  motivo: string;
  servicioId?: string;
  vehiculoId?: string;
  conductorId?: string;
  fechaServicio?: string;
  fechaFinServicio?: string;
  horaSalidaPrevista?: string;
  cambios?: {
    origenId?: string;
    destinoId?: string;
    naturaleza?: string;
    cantidad?: number;
    unidadCantidad?: UnidadCantidad;
    bultos?: number;
    observaciones?: string;
  };
}

/**
 * Payload para `POST /deca` (creación atómica de envío + emisión del DeCA
 * en una sola llamada). Coincide con `CrearDeCaDto` del backend.
 *
 * Sprint X: los datos de ruta + material + bultos pasan al nuevo campo
 * `servicios[]` (1..N). El envío ya no tiene origenId/destinoId ni
 * detalleMercancia — esos campos viven en Servicio.
 *
 * En MVP la UI envía exactamente 1 servicio. El modelo soporta N para
 * evolucionar la UI sin romper el backend.
 */
export interface CrearDeCAInput {
  tipoServicio: EnvioTipoServicio;
  cargadorId: string;
  transportistaId: string;
  vehiculoId: string;
  conductorId: string;
  fechaServicio: string;
  fechaFinServicio?: string;
  horaSalidaPrevista?: string;
  observaciones?: string;
  detalleViajero?: DetalleViajeroDto;
  servicios: Array<{
    origenId: string;
    destinoId: string;
    naturaleza: string;
    cantidad: number;
    unidadCantidad: UnidadCantidad;
    bultos: number;
    observaciones?: string;
  }>;
}

// ============================================================
// Empresa
// ============================================================

export type EmpresaTipo = 'CARGADOR' | 'TRANSPORTISTA' | 'AMBOS';

export interface Empresa {
  id: string;
  tipo: EmpresaTipo;
  razonSocial: string;
  nombreComercial?: string;
  nifCif: string;
  domicilioFiscal: string;
  municipio?: string;
  codigoPostal?: string;
  provincia?: string;
  pais?: string;
  email?: string;
  telefono?: string;
  numero?: string;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmpresaDto {
  tipo: EmpresaTipo;
  razonSocial: string;
  nombreComercial?: string;
  nifCif: string;
  domicilioFiscal: string;
  municipio?: string;
  codigoPostal?: string;
  provincia?: string;
  pais?: string;
  email?: string;
  telefono?: string;
}

export type UpdateEmpresaDto = Partial<CreateEmpresaDto> & { activo?: boolean };

// ============================================================
// Vehículo
// ============================================================

export interface Vehiculo {
  id: string;
  matricula: string;
  matriculaRemolque?: string;
  marca?: string;
  modelo?: string;
  tara?: number;
  mma?: number;
  formatoMatricula?: 'nuevo' | 'antiguo';
  formatoMatriculaRemolque?: 'nuevo' | 'antiguo';
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVehiculoDto {
  matricula: string;
  matriculaRemolque?: string;
  marca?: string;
  modelo?: string;
  tara?: number;
  mma?: number;
}

export type UpdateVehiculoDto = Partial<CreateVehiculoDto> & { activo?: boolean };

// ============================================================
// Conductor
// ============================================================

export interface Conductor {
  id: string;
  nombre: string;
  dni?: string;
  direccion?: string;
  telefono?: string;
  email?: string;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateConductorDto {
  nombre: string;
  dni?: string;
  direccion?: string;
  telefono?: string;
  email?: string;
}

export type UpdateConductorDto = Partial<CreateConductorDto> & { activo?: boolean };

// ============================================================
// Usuario (backoffice)
// ============================================================

/**
 * Roles validos en DeCA. Mantener sincronizado con
 * `deca-backend/src/auth/roles.enum.ts`.
 */
export type UsuarioRol = 'ADMIN' | 'OPERADOR' | 'CONSULTA' | 'CONDUCTOR';

export interface Usuario {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  role: UsuarioRol;
  active: boolean;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUsuarioDto {
  email: string;
  name: string;
  role: UsuarioRol;
  /** Password inicial (>=8 chars). El usuario deberia cambiarla al primer login. */
  password: string;
}

export interface UpdateUsuarioDto {
  email?: string;
  name?: string;
  role?: UsuarioRol;
  active?: boolean;
  /** Reset de password opcional (>=8 chars). Si llega vacio o undefined, se ignora. */
  password?: string;
}

// ============================================================
// Origen / Destino (catálogo)
// ============================================================

export interface OrigenDestino {
  id: string;
  nombre: string;
  direccion?: string;
  codigoPostal?: string;
  poblacion?: string;
  provincia?: string;
  pais?: string;
  nifCif?: string;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrigenDestinoDto {
  nombre: string;
  direccion?: string;
  codigoPostal?: string;
  poblacion?: string;
  provincia?: string;
  pais?: string;
  nifCif?: string;
}

export type UpdateOrigenDestinoDto = Partial<CreateOrigenDestinoDto> & { activo?: boolean };

// ============================================================
// Servicio (operación de transporte dentro de un DeCA)
// ============================================================

/**
 * Un Servicio es una operación de transporte concreta: una carga en X,
 * descarga en Y, con N bultos y M kg. Un DeCA puede tener N servicios
 * (ej. recoge en Madrid, descarga en Zaragoza, vuelve a cargar para
 * Barcelona). En MVP la UI crea 1 servicio por DeCA, pero el modelo
 * soporta varios desde el inicio.
 *
 * Origen/destino son FKs al catalogo `OrigenDestino` del tenant — NO
 * son texto libre.
 */
export interface Servicio {
  id: string;
  tenantId: string;
  decaId: string;
  origenId: string;
  destinoId: string;
  naturaleza: string;
  cantidad: number;
  unidadCantidad: UnidadCantidad;
  bultos: number;
  observaciones?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServicioDto {
  /** UUID del OrigenDestino (no texto libre). */
  origenId: string;
  /** UUID del OrigenDestino (no texto libre). */
  destinoId: string;
  naturaleza: string;
  cantidad: number;
  unidadCantidad: UnidadCantidad;
  bultos: number;
  observaciones?: string;
}

export type UpdateServicioDto = Partial<CreateServicioDto>;

// ============================================================
// Envío
// ============================================================

/**
 * Tipos de servicio legales según Orden FOM/2861/2012 + Ley 9/2025.
 * Mantener sincronizado con `deca-backend/src/envio/dto/envio.dto.ts`.
 */
export type EnvioTipoServicio =
  'MERCANCIA' | 'VIAJERO_DISCRECIONAL' | 'VIAJERO_TURISTICO' | 'COLABORACION';

export type EnvioEstado =
  'BORRADOR' | 'EMITIDO' | 'EN_CURSO' | 'FINALIZADO' | 'CERRADO' | 'CANCELADO';

/**
 * Cabecera de viaje.
 *
 * Sprint X: ya NO incluye origen/destino/detalleMercancia — esos
 * campos viven ahora en el recurso `Servicio` (1 DeCA → N Servicios).
 * El Envio queda como cabecera: tipo, cargador, transportista, vehiculo,
 * conductor y fecha(s).
 */
export interface Envio {
  id: string;
  tipoServicio: EnvioTipoServicio;
  cargadorId: string;
  transportistaId: string;
  vehiculoId: string;
  conductorId: string;
  fechaServicio: string;
  fechaFinServicio?: string;
  horaSalidaPrevista?: string;
  observaciones?: string;
  /** Solo presente si tipoServicio <> MERCANCIA. */
  detalleViajero?: DetalleViajeroDto;
  estado: EnvioEstado;
  decaId?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface DetalleViajeroDto {
  modalidad: string;
  contratanteNombre: string;
  contratanteNif?: string;
  numeroViajeros: number;
}

export interface CreateEnvioDto {
  tipoServicio: EnvioTipoServicio;
  cargadorId: string;
  transportistaId: string;
  vehiculoId: string;
  conductorId: string;
  fechaServicio: string;
  fechaFinServicio?: string;
  horaSalidaPrevista?: string;
  observaciones?: string;
  detalleViajero?: DetalleViajeroDto;
}

export type UpdateEnvioDto = Partial<CreateEnvioDto> & {
  estado?: EnvioEstado;
};
